/**
 * NOTE: 보청기 브랜드 목록 API에서 가져옴.
 * - brandsMixin.js를 사용하여 동적으로 로드
 * - /api/v1/products/brands/hearingaid API 활용
 * - 하드코딩된 BRAND_OPTIONS는 제거됨
 */

import { sanitizeCustomerName, sanitizeContact, validatePhoneNumber } from './consultationUtils';

// ====================
// 날짜 검증 함수
// ====================

/**
 * 날짜 문자열이 유효한지 검증
 * @param {string} dateString - YYYY-MM-DD 형식의 날짜 문자열
 * @returns {boolean}
 */
export function isValidDate(dateString) {
    if (!dateString) return false;
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date);
}

/**
 * 날짜의 시간을 00:00:00.000으로 초기화
 * @param {Date} date
 * @returns {Date}
 */
function normalizeDate(date) {
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);
    return normalized;
}

/**
 * 미래 날짜인지 검증 (오늘은 미래가 아님)
 * @param {string} dateString - YYYY-MM-DD 형식의 날짜 문자열
 * @returns {boolean}
 */
export function isFutureDate(dateString) {
    const today = normalizeDate(new Date());
    const inputDate = normalizeDate(new Date(dateString));
    return inputDate > today;
}

/**
 * 과거 날짜인지 검증 (오늘은 과거가 아님)
 * @param {string} dateString - YYYY-MM-DD 형식의 날짜 문자열
 * @returns {boolean}
 */
export function isPastDate(dateString) {
    const today = normalizeDate(new Date());
    const inputDate = normalizeDate(new Date(dateString));
    return inputDate < today;
}

/**
 * 날짜1이 날짜2보다 이전인지 검증
 * @param {string} date1 - YYYY-MM-DD 형식의 날짜 문자열
 * @param {string} date2 - YYYY-MM-DD 형식의 날짜 문자열
 * @returns {boolean}
 */
export function isDateBefore(date1, date2) {
    return new Date(date1) < new Date(date2);
}

/**
 * 날짜1이 날짜2보다 이후인지 검증
 * @param {string} date1 - YYYY-MM-DD 형식의 날짜 문자열
 * @param {string} date2 - YYYY-MM-DD 형식의 날짜 문자열
 * @returns {boolean}
 */
export function isDateAfter(date1, date2) {
    return new Date(date1) > new Date(date2);
}

// ====================
// 정품 인증 코드 검증
// ====================

/**
 * 정품 인증 코드 입력 검증 (영문, 숫자만 허용)
 * @param {string} value
 * @returns {string}
 */
export function sanitizeAuthCode(value) {
    return value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
}

/**
 * 정품 인증 코드 유효성 검사
 * @param {string} code
 * @returns {boolean}
 */
export function validateAuthCode(code) {
    return code && code.length >= 4 && code.length <= 20;
}

// ====================
// 개인정보 마스킹
// ====================

/**
 * 고객명 마스킹 (조회 결과 표출용)
 *
 * 조회 대상이 본인이 맞는지 확인할 수 있어야 하므로 맨 앞·맨 뒤 글자만 남기고
 * 가운데를 '*' 로 가린다.
 *  - 1자: 홍       → 홍
 *  - 2자: 길동     → 길*
 *  - 3자: 홍길동   → 홍*동
 *  - 4자 이상: 남궁길동 → 남**동
 *
 * @param {string} name
 * @returns {string}
 */
export function maskCustomerName(name) {
    const value = String(name == null ? '' : name).trim();

    if (!value) return '';
    if (value.length === 1) return value;
    if (value.length === 2) return `${value[0]}*`;

    return `${value[0]}${'*'.repeat(value.length - 2)}${value[value.length - 1]}`;
}

// ====================
// 폼 데이터 초기화
// ====================

/**
 * 정품 등록 폼 데이터 초기화
 */
export function getInitialRegistrationFormData() {
    return {
        storeCode: '',
        storeName: '',
        assistiveDeviceType: '해당',
        hearingAidDirection: '양측',
        hearingAidModel: '',
        customerName: '',
        buyDate: '',
        warrantyExpirationDate: '',
        productAuthCode: '',
        hearingAidBrand: '',
    };
}

/**
 * AS 안심 서비스 조회 폼 데이터 초기화
 */
export function getInitialInquiryFormData() {
    return {
        customerName: '',
        productAuthCode: '',
    };
}

// ====================
// 폼 유효성 검증
// ====================

/**
 * 정품 등록 폼 유효성 검증
 * @param {Object} formData
 * @returns {boolean}
 */
export function validateRegistrationForm(formData) {
    // 필수 필드 검증
    if (!formData.storeCode.trim()) {
        alert('가맹점 코드를 입력해주세요.');
        return false;
    }

    if (!formData.storeName.trim()) {
        alert('가맹점 명을 입력해주세요.');
        return false;
    }

    if (!formData.hearingAidBrand) {
        alert('보청기 브랜드를 선택해주세요.');
        return false;
    }

    if (!formData.assistiveDeviceType) {
        alert('보장구 여부를 선택해주세요.');
        return false;
    }

    if (!formData.hearingAidDirection) {
        alert('보청기 방향을 선택해주세요.');
        return false;
    }

    if (!formData.hearingAidModel.trim()) {
        alert('모델명을 입력해주세요.');
        return false;
    }

    if (!formData.customerName.trim()) {
        alert('고객명을 입력해주세요.');
        return false;
    }

    // 구매일자 검증
    if (!formData.buyDate) {
        alert('구매일자를 입력해주세요.');
        return false;
    }

    if (!isValidDate(formData.buyDate)) {
        alert('올바른 구매일자를 입력해주세요.');
        return false;
    }

    if (isFutureDate(formData.buyDate)) {
        alert('구매일자는 미래 날짜일 수 없습니다.');
        return false;
    }

    // 보증만료 날짜 검증
    if (!formData.warrantyExpirationDate) {
        alert('보증만료 날짜를 입력해주세요.');
        return false;
    }

    if (!isValidDate(formData.warrantyExpirationDate)) {
        alert('올바른 보증만료 날짜를 입력해주세요.');
        return false;
    }

    // if (!isDateAfter(formData.warrantyExpirationDate, formData.buyDate)) {
    //     alert('보증만료 날짜는 구매일자보다 이후여야 합니다.');
    //     return false;
    // }

    // 정품 인증 코드 검증
    if (!formData.productAuthCode.trim()) {
        alert('인증코드를 입력해주세요.');
        return false;
    }

    if (!validateAuthCode(formData.productAuthCode)) {
        alert('인증코드는 4~20자의 영문, 숫자로 입력해주세요.');
        return false;
    }

    return true;
}

/**
 *  AS 안심 서비스 조회 폼 유효성 검증
 * @param {Object} formData
 * @returns {boolean}
 */
export function validateInquiryForm(formData) {
    if (!formData.customerName.trim()) {
        alert('고객명을 입력해주세요.');
        return false;
    }

    if (!formData.productAuthCode.trim()) {
        alert('인증코드를 입력해주세요.');
        return false;
    }

    if (!validateAuthCode(formData.productAuthCode)) {
        alert('인증코드는 4~20자의 영문, 숫자로 입력해주세요.');
        return false;
    }

    return true;
}

// 상담 예약 폼 데이터 초기화
export function getInitialFormData() {
    return {
        storeCode : '',
        storeName : '',
        assistiveDeviceType: '해당',
        hearingAidDirection : '양측',
        hearingAidModel : '',
        customerName: '',
        buyDate: '',
        warrantyExpirationDate : '',
        productAuthCode : '',
        contact: '',
        visitRegion: '서울',
        hearingAidBrand: '', // API에서 브랜드 목록을 가져오므로 빈 문자열로 초기화
        privacyConsent: false
    };
}

// 공통 watch 설정을 위한 함수
export function setupFormWatchers(component) {
    return {
        'formData.customerName'(value) {
            const result = sanitizeCustomerName(value);
            component.$nextTick(() => {
                component.formData.customerName = result;
            });
        },
        'formData.contact'(value) {
            const result = sanitizeContact(value);
            component.$nextTick(() => {
                component.formData.contact = result;
            });
        }
    };
}
